<template>
    <div class="multi-chat-viewer" @click="(e) => onSingleChatClicked(e)">
        <div class="toolbar">
            <div class="toolbar-left">
                <span class="title">Chats on {{ getSelectedAgentName() }}</span>
                <CircleUserSelect :users="getChatUsers()" :value="multiChatView.type" @input="changeAgentGroup" />
            </div>
            <div class="actions">
                <SupsisButton primary @click="close">
                    <i class="material-icons">close</i>
                    {{ $t("message.close") }}
                    <span class="shortcut-keyboard dark">ESC</span>
                </SupsisButton>
            </div>
        </div>
        <div class="selection-bar" v-if="selectionMode">
            <span>{{ selectedContacts.length }} chat seçildi</span>
            <div class="actions">
                <SupsisButton @click="selectAllChats" ghost>
                    <i class="material-icons">select_all</i>
                    {{ $t("message.selectAllPermission") }}
                    <span class="shortcut-keyboard dark">{{ isMac ? "⌘A" : "Alt+A" }}</span>
                </SupsisButton>
                <SupsisButton @click="closeSelectedChats">
                    <i class="material-icons">stop</i>
                    {{ $t("message.speechTermination") }}
                    <span class="shortcut-keyboard dark">{{ isMac ? "⌘S" : "Alt+S" }}</span>
                </SupsisButton>
                <SupsisButton @click="redirectSelectedChats">
                    <i class="material-icons">forward</i>
                    {{ $t("message.forwardRep") }}
                    <span class="shortcut-keyboard dark">{{ isMac ? "⌘Y" : "Alt+Y" }}</span>
                </SupsisButton>
                <SupsisButton @click="assignSelectedChats">
                    <i class="material-icons">person_add</i>
                    {{ $t("message.addRepresentative") }}
                    <span class="shortcut-keyboard dark">{{ isMac ? "⌘E" : "Alt+T" }}</span>
                </SupsisButton>
                <SupsisButton @click="cancelSelection" danger>
                    <i class="material-icons">cancel</i>
                    {{ $t("message.cancel") }}
                    <span class="shortcut-keyboard dark">{{ isMac ? "⌘V" : "Alt+V" }}</span>
                </SupsisButton>
            </div>
        </div>
        <div class="chats" ref="chatsScrollContent">
            <div
                class="single-chat"
                v-for="contact in getContacts()"
                :key="contact.id"
                @click="(e) => onSingleChatClicked(e, contact)"
                :class="{ focused: focusedContact === contact._id }"
            >
                <div class="single-chat-header" @mouseenter="onHeaderMouseEnter" @mouseleave="onHeaderMouseLeave">
                    <div class="left" @click.stop="toggleSelectContact(contact)">
                        <div class="select-icon">
                            <div class="circle-icon">
                                <i v-if="!isHovered && !selectionMode" class="material-icons">person</i>
                                <div
                                    v-else
                                    class="circle-checkbox"
                                    :class="{ 'is-checked': isContactSelected(contact) }"
                                >
                                    <i v-if="isContactSelected(contact)" class="material-icons">check</i>
                                </div>
                            </div>
                        </div>
                        <span>{{ contact.fullname }}</span>
                    </div>
                    <div class="right">
                        <SupsisButton ghost @click="() => showProfile(contact)" small>
                            <i class="material-icons">info</i>
                            Profile
                        </SupsisButton>
                    </div>
                </div>
                <div
                    class="contact-detail-wrapper"
                    v-if="showingProfileContactId === contact._id && focusedContact === contact._id"
                >
                    <ContactDetail :contact="contact" :show-profile-top="false" />
                    <a-divider />
                    <ContactProperties :contact="contact" :show-toolbar="false" />
                </div>

                <ChatWindow
                    ref="chatWindow"
                    :style="{ width: chatWindowWidth }"
                    :is-live="true"
                    :contact="contact"
                    :show-header="false"
                />
            </div>
            <div class="empty" v-if="getContacts().length === 0">
                <h1>No chat found</h1>
                <p>There is no chat for this agent</p>
            </div>
        </div>

        <Modal2
            v-if="assignAgent"
            id="assign-agent"
            :title="$t('message.addRepresentative')"
            :show-footer="false"
            @cancel="assignAgent = false"
        >
            <AgentList
                :assign="true"
                :show-online-agents="true"
                @close="() => closeModal('assign')"
                :default-contacts="selectedContacts"
            />
        </Modal2>
        <Modal2
            v-if="forwardAgent"
            id="forward-agent"
            :title="$t('message.forwardRepresentative')"
            :show-footer="false"
            @cancel="forwardAgent = false"
        >
            <AgentList
                :forward="true"
                :show-online-agents="true"
                @close="() => closeModal('forward')"
                :default-contacts="selectedContacts"
            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import ChatWindow from "../ChatWindow.vue";
import log2 from "../../../../utils/log2";
import SupsisButton from "../../../../views/Pages/Settings/components/SupsisButton.vue";
import _ from "lodash";
import ContactDetail from "../ContactDetail.vue";
import ContactProperties from "../ContactProperties.vue";
import CircleUserSelect from "./CircleUserSelect.vue";
import { message } from "ant-design-vue";
import AgentList from "../../../../components/AgentList.vue";
import Modal2 from "../../../common/Modal2.vue";

const log = log2("MultiChatViewer");
export default Vue.extend({
    name: "MultiChatViewer",
    components: { CircleUserSelect, ContactProperties, ContactDetail, SupsisButton, ChatWindow, Modal2, AgentList },
    computed: {
        ...mapState({
            multiChatView: (state) => state.contact.multiChatView,
            users: (state) => state.user.users.data,
            stats: (state) => state.chat.stats,
        }),
    },

    data() {
        return {
            chatWindowWidth: `calc(100%)`,
            focusedContact: null,
            showingProfileContactId: null,
            selectionMode: false,
            selectedContacts: [],
            isHovered: false,
            assignAgent: false,
            forwardAgent: false,
            isMac: /Mac|iPod|iPhone|iPad/.test(navigator.platform),
        };
    },
    methods: {
        ...mapActions({
            destroyContactConversation: "chat/destroyContactConversation",
            setActiveView: "contact/setActiveView",
        }),
        changeAgentGroup(type) {
            log("changeAgentGroup", type);
            this.$emit("change-agent-group", type);
        },
        showProfile(contact) {
            if (this.showingProfileContactId === contact._id) {
                this.showingProfileContactId = null;
                return;
            }
            this.showingProfileContactId = contact._id;
        },
        getChatUsers() {
            const { usersChatCounts } = this.stats;
            return Object.keys(usersChatCounts)
                .map((key) => {
                    return {
                        user: this.users.find((x) => x._id === key),
                        badge: usersChatCounts[key],
                    };
                })
                .filter((x) => !!x.user);
        },

        getContacts() {
            return _.orderBy(this.multiChatView.contacts, ["lastConversation.createdDate"], ["asc"]);
        },
        close() {
            this.setActiveView("tab");
            this.$emit("close");
        },
        onSingleChatClicked(e, contact) {
            log("onSingleChatClicked", contact);
            e.stopPropagation();
            if (contact) {
                this.focusedContact = contact._id;
            } else {
                if (this.focusedContact) this.focusedContact = null;
            }
        },
        getSelectedAgentName() {
            const type = this.multiChatView.type;
            if (type === "chatbot") return "Chatbot";
            if(type === "unassign") return "Unassigned";
            return this.users.find((x) => x._id === type)?.name;
        },
        toggleSelectContact(contact) {
            if (!this.selectionMode) {
                this.selectionMode = true;
            }
            const index = this.selectedContacts.findIndex((c) => c._id === contact._id);
            if (index === -1) {
                this.selectedContacts.push(contact);
            } else {
                this.selectedContacts.splice(index, 1);
            }
            if (this.selectedContacts.length === 0) {
                this.selectionMode = false;
            }
        },
        isContactSelected(contact) {
            return this.selectedContacts.some((c) => c._id === contact._id);
        },
        async closeSelectedChats() {
            try {
                for (const contact of this.selectedContacts) {
                    await this.destroyContactConversation(contact);
                }
                message.success(`${this.selectedContacts.length} chat başarıyla kapatıldı.`);
                this.selectedContacts = [];
                this.selectionMode = false;
            } catch (error) {
                message.error("Chatları kapatırken bir hata oluştu. Lütfen tekrar deneyin.");
            }
        },
        redirectSelectedChats() {
            this.forwardAgent = true;
        },
        assignSelectedChats() {
            this.assignAgent = true;
        },
        closeModal(type) {
            if (type === "assign") {
                this.assignAgent = false;
            } else if (type === "forward") {
                this.forwardAgent = false;
            }
            this.selectedContacts = [];
            this.selectionMode = false;
        },
        onHeaderMouseEnter() {
            this.isHovered = true;
        },
        onHeaderMouseLeave() {
            this.isHovered = false;
        },
        selectAllChats() {
            this.selectedContacts = this.getContacts();
        },
        cancelSelection() {
            this.selectedContacts = [];
            this.selectionMode = false;
        },
        handleKeyboardShortcuts(e) {
            log("handleKeyboardShortcuts", e);
            const isMacCmd = this.isMac && e.metaKey;
            const isWindowsAlt = !this.isMac && e.altKey;

            if (isMacCmd || isWindowsAlt) {
                switch (e.key.toLowerCase()) {
                    case "a":
                        e.preventDefault();
                        this.selectAllChats();
                        break;
                    case "s":
                        e.preventDefault();
                        this.closeSelectedChats();
                        break;
                    case "y":
                        e.preventDefault();
                        this.redirectSelectedChats();
                        break;
                    case "e":
                        if (this.isMac) {
                            e.preventDefault();
                            this.assignSelectedChats();
                        }
                        break;
                    case "t":
                        if (!this.isMac) {
                            e.preventDefault();
                            this.assignSelectedChats();
                        }
                        break;
                    case "v":
                        e.preventDefault();
                        this.cancelSelection();
                        break;
                }
            }
        },
    },
    watch: {
        multiChatView: {
            handler() {
                const currentContacts = this.getContacts();

                // Seçili kontakları güncelle
                this.selectedContacts = this.selectedContacts.filter((selectedContact) =>
                    currentContacts.some((contact) => contact._id === selectedContact._id)
                );

                // Eğer odaklanılan kontak artık mevcut değilse, odağı kaldır
                if (this.focusedContact && !currentContacts.some((contact) => contact._id === this.focusedContact)) {
                    this.focusedContact = null;
                }

                // Seçim modunu güncelle
                if (this.selectedContacts.length === 0) {
                    this.selectionMode = false;
                }
            },
            deep: true,
        },
        "$route"() {
            if (this.multiChatView.enabled) {
                this.$emit("close");
            }
        },
    },
    mounted() {
        const keydownHandler = (e) => {
            if (e.key === "Escape") {
                if (this.focusedContact) {
                    this.focusedContact = null;
                } else {
                    this.close();
                }
            }
            // ALT + P show profile ( support macos
            if (e.altKey && e.key === "p") {
                if (this.focusedContact) {
                    const contact = this.getContacts().find((x) => x._id === this.focusedContact);
                    this.showProfile(contact);
                }
            }
        };
        window.addEventListener("keydown", keydownHandler);
        window.addEventListener("keydown", this.handleKeyboardShortcuts);
        this.$once("hook:beforeDestroy", () => {
            window.removeEventListener("keydown", keydownHandler);
            window.removeEventListener("keydown", this.handleKeyboardShortcuts);
        });
    },
});
</script>

<style lang="scss">
.multi-chat-viewer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    background-color: #fff;
    width: 100%;
    height: 100%;
    overflow: hidden;

    .shortcut-keyboard {
        font-size: 0.8rem;
        font-weight: bold;
        color: #000;
        background-color: #f1f1f1;
        padding: 2px 5px;
        border-radius: 5px;
        border: 1px solid #ccc;
        margin-left: 5px;
    }

    .md-progress-bar {
        position: absolute;
        top: 0;
        width: 100%;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;

        .toolbar-left {
            display: flex;
            align-items: center;
            gap: 20px;
            flex: 1;
        }

        .title {
            font-size: 20px;
            font-weight: 600;
            color: #22272d;
            white-space: nowrap;
        }

        .actions {
            display: flex;
            gap: 10px;
            align-items: center;
        }
    }

    .selection-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px;
        background-color: #f0f0f0;
        border-bottom: 1px solid #ccc;

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .chats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        height: calc(100% - 85px);
        width: 100%;
        overflow-x: auto;

        .empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: 100%;
            height: 100%;

            h1 {
                font-size: 24px;
                font-weight: bold;
            }

            p {
                font-size: 18px;
            }
        }

        .single-chat {
            width: 400px;
            height: calc(100% - 30px);
            max-height: calc(100% - 30px);
            min-height: calc(100% - 30px);
            display: flex;
            flex-direction: column;
            position: relative;

            .contact-detail-wrapper {
                position: absolute;
                background: white;
                right: 0px;
                top: 57px;
                z-index: 2;
                border: 1px solid grey;
                height: calc(100% - 180px);
                overflow-y: scroll;
            }

            .single-chat-header {
                display: flex;
                justify-content: space-between;
                padding: 10px;
                border: 1px solid gray;
                border-top-left-radius: 5px;
                border-top-right-radius: 5px;
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: #f0f0f0;
                    .circle-icon {
                        i {
                            opacity: 0;
                        }
                        .circle-checkbox {
                            opacity: 1;
                        }
                    }
                }

                .left {
                    display: flex;
                    align-items: center;
                    gap: 10px;

                    .select-icon {
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 24px;
                        height: 24px;

                        .circle-icon {
                            position: relative;
                            width: 24px;
                            height: 24px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            transition: all 0.2s ease;
                            border-radius: 50%;

                            i {
                                position: absolute;
                                font-size: 20px;
                                color: #666;
                                transition: opacity 0.2s ease;
                            }

                            .circle-checkbox {
                                position: absolute;
                                width: 24px;
                                height: 24px;
                                border: 2px solid #666;
                                border-radius: 50%;
                                transition: all 0.2s ease;
                                display: flex;
                                align-items: center;
                                justify-content: center;

                                &.is-checked {
                                    background-color: #1976d2;
                                    border-color: #1976d2;

                                    i {
                                        color: white;
                                        font-size: 18px;
                                    }
                                }
                            }
                        }
                    }

                    span {
                        font-size: 18px;
                        font-weight: bold;
                        -webkit-line-clamp: 1;
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                .right {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }

                //single-line
            }

            &.focused {
                width: 1200px;
            }
        }

        #chat-window-wrapper {
            flex: 1 1 200px;
            width: calc(100%);
            border: 1px solid gray;
            padding: 0 5px;
            height: inherit !important;
        }
    }
}
</style>
